'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight } from 'lucide-react';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';
import type { ServiceAreaData } from '../../data';

const SECTION_ID = 'town-links';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  },
};

const cardMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

interface TownLinksProperties {
  towns: ServiceAreaData[];
}

function TownLinks({ towns }: TownLinksProperties) {
  const sorted = [...towns].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <SectionBase id={SECTION_ID} background="bg-card">
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        <SectionHeader
          subtitle="EXPLORE BY TOWN"
          title="Our Priority Service Areas"
          description="Click on a community below to learn more about how Cornerstone Homes serves homeowners in that area."
        />

        <div
          className={cls`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full`}
        >
          {sorted.map((town) => (
            <motion.div key={town.slug} {...cardMotionProps}>
              <Link
                href={`/about/our-service-areas/${town.slug}`}
                className="group flex flex-col gap-3 p-6 rounded-lg bg-background hover:bg-primary/5 border border-border transition-colors h-full"
              >
                <div className="flex items-center gap-2">
                  <MapPin className="text-primary size-5" />
                  <span className="font-prata font-semibold text-foreground group-hover:text-primary transition-colors">
                    {town.name}
                  </span>
                </div>
                <TypographySmall className="text-muted-foreground leading-relaxed flex-1">
                  {town.tagline}
                </TypographySmall>
                <div className="flex items-center gap-1 text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>Learn more</span>
                  <ArrowRight className="size-4" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </SectionBase>
  );
}

export default TownLinks;
